import { z } from "zod";
import { AuthContextSchema, loginMethod } from "./auth-models";

// #region ADMIN REQUEST TYPES

// ADMIN USER SEARCH
export enum AdminUserSearchSortKey {
  Name = "firstName",
  Email = "email",
}

export const AdminUserSearchRequestSchema = z.object({
  query: z.string().nullable(),
  app: z.string().nullable(),
  sortBy: z.object({
    direction: z.string(),
    key: z.string().optional(),
  }),
  limit: z.number().min(1).max(100),
  offset: z.number().min(0),
});

export type AdminUserSearchRequest = z.infer<
  typeof AdminUserSearchRequestSchema
>;

export type AdminUserSearchSort = z.infer<
  typeof AdminUserSearchRequestSchema.shape.sortBy
>;

// ADMIN ORGS
export const AdminOrgSearchRequestSchema = z.object({
  query: z.string().nullable(),
  limit: z.number().min(1).max(100),
  offset: z.number().min(0),
});

export type AdminOrgSearchRequest = z.infer<typeof AdminOrgSearchRequestSchema>;

/** A workspace is the org's sign-in subdomain, so it must be a valid hostname label */
export const WORKSPACE_REGEX = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

/** Upstream as the only login method with no providers leaves nothing to sign in with */
export const hasSignInOption = ({
  loginMethods,
  upstreamProviders,
}: {
  loginMethods: string[];
  upstreamProviders: string[];
}) => loginMethods.some((m) => m !== "UPSTREAM") || !!upstreamProviders.length;

export const AdminUpdateOrgRequestSchema = z
  .object({
    orgId: z.string().min(1),
    /** Empty string clears it */
    workspace: z.string(),
    loginMethods: z.array(loginMethod).min(1),
    upstreamProviders: z.array(z.string()),
  })
  .refine(hasSignInOption, {
    path: ["upstreamProviders"],
    message: "Select an upstream provider or another login method",
  });

export type AdminUpdateOrgRequest = z.infer<typeof AdminUpdateOrgRequestSchema>;

// OTHERS
export type GetSelectedUserRequest = {
  sub: string;
};

export type GetUserLogsRequest = {
  sub: string;
};

export type RemoveMFARequest = {
  sub: string;
  adminNotes: string;
};
export type UpdateUserAttributesRequest<T> = {
  sub: string;
  attributes: T;
  adminNotes: string;
};

export type ModifyUserStatusRequest = {
  sub: string;
  adminNotes: string;
  currentStatus: boolean;
};

export type TerminateUserRequest = {
  sub: string;
  adminNotes: string;
};

export type GetProfilePictureDataRequest = {
  sub: string;
};

export type RemoveProfilePictureRequest = {
  sub: string;
  adminNotes: string;
};

export type MigrateUserRequest = {
  sub: string;
  appId: string;
  adminNotes: string;
};
// #endregion ADMIN REQUEST TYPES

// #region ACCOUNT REQUEST TYPES
export type UpdateNameRequest = {
  given_name: string;
  family_name: string;
};

export type UpdateCompanyRequest = {
  "custom:company_name"?: string;
};

export interface IChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface IChangeEmailRequest {
  email: string;
}

// #region MFA TYPES
export type SendSmsCodeMfaRequest = {
  phoneNumber: string;
};

export type ConfirmSmsFactorMfaRequest = {
  phoneNumber: string;
  verificationCode: string;
};

export type VerifySmsCodeMfaRequest = {
  rememberMe: boolean;
  verificationCode: string;
  tempToken?: string;
};

export type GenerateTotpFactorMfaRequest = {
  requestToken: string;
};

export type ConfirmTotpFactorMfaRequest = {
  payload: string;
};

export type VerifyTotpCodeMfaRequest = {
  rememberMe: boolean;
  tempToken: string;
  payload: string;
};

export type DeactivateSmsFactorMfaRequest = {
  verificationCode: string;
};

export type DeactivateTotpFactorMfaRequest = {
  payload: string;
};
// #endregion ACCOUNT REQUEST TYPES

export type IUserLogItem = {
  cognitoUserId: string;
  logKey: string;
  logDate: number;
  userAgent?: string;
  ip: string;
  params: { [key: string]: string | null }[];
  adminNotes?: string;
};

export type UpdateProfilePictureRequest = {
  user: File | null;
  original?: File;
  rotation: number;
  xCoord: number;
  yCoord: number;
  zoom: number;
  sub?: string;
  adminNotes?: string;
};

export const GetAuthContextRequestSchema = AuthContextSchema.pick({
  appId: true,
  workspace: true,
  follow: true,
});
export type GetAuthContextRequest = z.infer<typeof GetAuthContextRequestSchema>;
